// Libs
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { z } from "zod";

// Local
import {
  adminPropDefaultsForCustomField,
  CustomFieldManager,
  UidCustomField,
} from "../../../models/custom-field";
import {
  TitleField,
  MinField,
  MaxField,
  OnTaskStatusField,
  isDefinedOnTaskStatus,
  HideOnCRCreationField,
} from "../../admin/AddCustomFieldDialog/AddCustomFieldDialog";
import { ErrorMessage } from "../../ErrorMessage";
import { OTaskTypes } from "../../../models/task-types";
import {
  ActionButtons,
  CreateSiteCustomFieldFormInterface,
  RequiredField,
  UidSchema_CSCF,
} from "./AddCreateSiteCustomFieldDialog";

const uidDefaultValues: Partial<UidInterface> = {
  min: null,
  max: null,

  title: "",
  editable: true,
  required: false,
  hideOnCraftRecordCreation: false,
};
type UidInterface = z.infer<typeof UidSchema_CSCF>;

export const UidCreateSiteCustomFieldForm: React.FunctionComponent<
  CreateSiteCustomFieldFormInterface
> = (props) => {
  const {
    control,
    formState: { errors },
    reset,
    handleSubmit,
  } = useForm<UidInterface>({
    defaultValues: uidDefaultValues,
    resolver: zodResolver(UidSchema_CSCF),
    mode: "onChange",
  });

  useEffect(() => {
    reset(uidDefaultValues);
  }, [reset]);

  const [displayError, setDisplayError] = useState<boolean>(false);
  const [displayWorkTypeError, setDisplayWorkTypeError] =
    useState<boolean>(false);

  const onSave: SubmitHandler<UidInterface> = (formValues) => {
    if (props.workType === undefined) {
      setDisplayWorkTypeError(true);
      return;
    }

    // Drop undefined values from the onTaskStatus list.
    // onTaskStatus itself will be undefined if this is a workType.
    const onTaskStatus = formValues.onTaskStatus?.filter(isDefinedOnTaskStatus);

    // Compose the output structure.
    let fullOutput: UidCustomField;

    const partialOutput: Omit<
      UidCustomField,
      "taskType" | "craftRecordOrTask"
    > = {
      ...adminPropDefaultsForCustomField(),
      fieldType: "uid",
      defaultValue: null,
      title: formValues.title,
      editable: true,
      required: formValues.required,
      min: formValues.min,
      max: formValues.max,
      craftType: props.workType,
    };

    // If taskType is null this means it's a craftRecord, and
    // we don't want to save the taskType to the database.
    if (props.taskType == null) {
      fullOutput = {
        ...partialOutput,
        craftRecordOrTask: "craftRecord",
        hideOnCraftRecordCreation: formValues.hideOnCraftRecordCreation,
      };
    } else {
      fullOutput = {
        ...partialOutput,
        craftRecordOrTask: "task",
        taskType: props.taskType,
        onTaskStatus: onTaskStatus,
      };
    }

    // Validate CustomFieldDocData
    const validatedOutput = CustomFieldManager.parse(fullOutput);
    props.handleSave(validatedOutput);
    props.closeDialog();
  };

  const errorMessage = (
    <ErrorMessage
      message="Something went wrong."
      clearMessage={() => setDisplayError(false)}
    />
  );

  const undefinedWorkTypeErrorMessage = (
    <ErrorMessage
      message="Work Type must be selected"
      clearMessage={() => setDisplayWorkTypeError(false)}
    />
  );

  return (
    <form
      className="contents"
      autoComplete="off"
      onSubmit={handleSubmit(onSave)}
    >
      {props.taskType != null ? (
        <div></div>
      ) : (
        <HideOnCRCreationField control={control} />
      )}

      <div className="col-span-1 sm:col-span-2">
        <TitleField control={control} errors={errors} />
      </div>

      {/* Specific to fieldType uid */}
      <MinField control={control} errors={errors} isStringBased />
      <MaxField control={control} errors={errors} isStringBased />

      <RequiredField control={control} />

      {props.taskType != null &&
        Object.values(OTaskTypes).includes(props.taskType) && (
          <OnTaskStatusField
            taskStatusLabel="Prompt On Task Status"
            control={control}
            taskStatusList={props.taskStatusList}
          />
        )}

      <span className="-my-5 h-10 justify-self-center sm:col-span-2">
        {displayError ? errorMessage : null}
        {displayWorkTypeError ? undefinedWorkTypeErrorMessage : null}
      </span>

      <ActionButtons onCancel={props.closeDialog} />
    </form>
  );
};
